// Voice sprite manifest and call rhythm (RALLY_GOAL.md §8). Pure: the same schedule drives
// playback in the browser and the call-timing tests, so the tests use the real clip durations.
import { DISTANCE_WORDS } from './notes';

export interface SpriteManifest {
  file: string;
  voice: string;
  sampleRate: number;
  gap: number;
  decodedLength: number;
  /** token → [start s, duration s] in the sprite. */
  tokens: Record<string, [number, number]>;
}

export interface ScheduledToken {
  token: string;
  /** Start time relative to the start of the call (s). */
  at: number;
  /** Offset and duration inside the sprite (s). */
  offset: number;
  dur: number;
}

/** Pause between words of one note, and around distances/links ("four left · one hundred"). */
export const WORD_GAP = 0.025;
export const PHRASE_GAP = 0.11;
/** Faster delivery for compressed calls at speed (real co-drivers speed up too). */
export const COMPRESSED_RATE = 1.08;

const DISTANCE_TOKENS = new Set(Object.values(DISTANCE_WORDS).flat());
const LINK_TOKENS = new Set(['and', 'into']);

function gapBefore(prev: string | undefined, t: string): number {
  if (!prev) return 0;
  if (DISTANCE_TOKENS.has(t) || DISTANCE_TOKENS.has(prev) || LINK_TOKENS.has(prev) || LINK_TOKENS.has(t)) return PHRASE_GAP;
  return WORD_GAP;
}

/** Lay out a call's tokens; `missing` collects tokens that have no clip. */
export function scheduleTokens(m: SpriteManifest, tokens: string[], rate = 1, missing?: string[]): { items: ScheduledToken[]; duration: number } {
  const items: ScheduledToken[] = [];
  let t = 0;
  let prev: string | undefined;
  for (const tok of tokens) {
    const clip = m.tokens[tok];
    if (!clip) {
      missing?.push(tok);
      continue;
    }
    t += gapBefore(prev, tok) / rate;
    items.push({ token: tok, at: t, offset: clip[0], dur: clip[1] });
    t += clip[1] / rate;
    prev = tok;
  }
  return { items, duration: t };
}

/** Spoken duration of a token sequence with the sprite's real clip lengths. */
export function spriteDuration(m: SpriteManifest, compressedRate = COMPRESSED_RATE): (tokens: string[], compressed?: boolean) => number {
  return (tokens, compressed = false) => {
    // Same arithmetic as scheduleTokens, without allocating the schedule (the caller asks every frame).
    const rate = compressed ? compressedRate : 1;
    let t = 0;
    let prev: string | undefined;
    for (const tok of tokens) {
      const clip = m.tokens[tok];
      if (!clip) continue;
      t += gapBefore(prev, tok) / rate + clip[1] / rate;
      prev = tok;
    }
    return t;
  };
}
